import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type QueryFunction,
} from '../common';
import {
  getManagedIntegrationDetailsKey,
  getManagedIntegrationListKey,
} from './keys';
import { ManagedIntegration, ManagedIntegrationList } from './types';

//Get a single managed integration
export const useManagedIntegration: QueryFunction<
  ManagedIntegration,
  [managedIntegrationUuid: string],
  ReturnType<typeof getManagedIntegrationDetailsKey>
> = (managedIntegrationUuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getManagedIntegrationDetailsKey(managedIntegrationUuid),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/${managedIntegrationUuid}`, {
        query,
        init: { signal },
      }),
  });
};

//Get the full list of managed integrations
export const useManagedIntegrationList: QueryFunction<
  ManagedIntegrationList[],
  [],
  ReturnType<typeof getManagedIntegrationListKey>
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getManagedIntegrationListKey(),
    queryFn: ({ signal }) =>
      request('/managed_integrations', {
        query:
          typeof query === 'string'
            ? query
            : {
                ...query,
                related_object_type_in:
                  query?.relatedObjectTypeIn?.join?.(',') ?? [],
              },
        init: { signal },
      }),
  });
};

/***************  MUTATIONS  ***************/

export const useCreateManagedIntegration: MutationFunction<
  ManagedIntegration,
  any
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    (body) => {
      return request(`/managed_integrations/`, {
        query,
        init: { method: 'POST', body },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['createManagedIntegration'],
      onSuccess: (data, requestData, ...rest) => {
        const key = getManagedIntegrationDetailsKey(data.uuid);
        queryClient.setQueryData(key, data);
        queryClient.invalidateQueries({
          queryKey: getManagedIntegrationListKey(),
        });
        queryOptions?.onSuccess?.(data, requestData, ...rest);
      },
    },
  );
};

//This deletes a managed integration, overwrites QueryData for that individual integration,
//and invalidates all related managed integration list queries
export const useDeleteManagedIntegration: MutationFunction<
  unknown,
  { uuid: string }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();

  return useMutation(
    (requestBody) => {
      return request(`/managed_integrations/${requestBody.uuid}`, {
        query,
        init: { method: 'DELETE' },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['deleteManagedIntegration'],
      onSuccess: (data, requestBody, ...rest) => {
        const key = getManagedIntegrationDetailsKey(requestBody.uuid);
        queryClient.setQueryData(key, data); // Do we need this?
        queryClient.invalidateQueries({
          queryKey: getManagedIntegrationListKey(),
        });
        queryOptions?.onSuccess?.(data, requestBody, ...rest);
      },
    },
  );
};

//This updates a managed integration, overwrites QueryData for that individual integration,
//and invalidates all related managed integration list queries
export const useEditManagedIntegration: MutationFunction<
  ManagedIntegration,
  { uuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();

  return useMutation(
    ({ uuid, body }) => {
      return request(`/managed_integrations/${uuid}`, {
        query,
        init: { method: 'PATCH', body },
      });
    },
    {
      ...queryOptions,
      mutationKey: ['editManagedIntegration'],
      onSuccess: (data, requestBody, ...rest) => {
        const key = getManagedIntegrationDetailsKey(requestBody.uuid);
        queryClient.setQueryData(key, data);
        queryClient.invalidateQueries({
          queryKey: getManagedIntegrationListKey(),
        });
        queryOptions?.onSuccess?.(data, requestBody, ...rest);
      },
    },
  );
};

export default {
  useManagedIntegration,
  useManagedIntegrationList,
  useCreateManagedIntegration,
  useDeleteManagedIntegration,
  useEditManagedIntegration,
};
